import { usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import type { Survey } from 'posthog-js';
import type { FunctionComponent } from 'react';

import { window } from '../../globals';

import { OnboardingSurveyFullscreen } from './OnboardingSurveyFullscreen';

/**
 * Gating wrapper for the post-signup onboarding survey takeover. On mount,
 * queries `posthog.getActiveMatchingSurveys` and looks for one whose id matches
 * `window.posthogOnboardingSurveyId`. If found, replaces children with
 * `OnboardingSurveyFullscreen`; otherwise passes children through unchanged.
 *
 * Mounted under `Application` (the authenticated-routes wrapper) rather than at
 * App root so its mount timing aligns with `posthog.identify` for both
 * fresh-page-loads and SPA-signup redirects — the fullscreen never appears
 * with stale anonymous survey data.
 */
const OnboardingSurvey: FunctionComponent = ({ children }) => {
  const posthog = usePostHog();
  const [activeSurvey, setActiveSurvey] = useState<Survey | null>(null);
  const surveyId = window.posthogOnboardingSurveyId;

  useEffect(() => {
    if (!posthog || !surveyId) {
      return undefined;
    }
    let isCancelled = false;
    posthog.getActiveMatchingSurveys((surveys) => {
      if (isCancelled) {
        return;
      }
      setActiveSurvey(surveys.find((s) => s.id === surveyId) ?? null);
    });
    return () => {
      isCancelled = true;
    };
  }, [posthog, surveyId]);

  if (activeSurvey) {
    return (
      <OnboardingSurveyFullscreen
        survey={activeSurvey}
        onClose={() => setActiveSurvey(null)}
      />
    );
  }
  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment -- It makes sense here for typing purposes.
    <>{children}</>
  );
};

export default OnboardingSurvey;
